<template>
  <fieldset id="provider-search">
    <div class="form-header">
      <h2 class="h4">Select Provider</h2>
      <p>* indicates required field</p>
    </div>

    <FormField labelText="How would you like to search for your provider?">
      <ul class="radio-group large">
        <li v-for="type in searchType" :key="`type-${type.display}`">
          <input type="radio" :id="`search-type-${type.returnValue}`" name="provider-search" :value="type.returnValue" v-model.lazy="v$.formResponse.searchType.$model" />
          <label :for="`search-type-${type.returnValue}`">{{ type.display }}</label>
        </li>
      </ul>
      <template v-if="!isCRM && refData.content.searchProviderHelpText" v-slot:helperText>
        <div v-html="refData.content.searchProviderHelpText.informationText" />
      </template>
    </FormField>

    <!-- Search by provider number -->
    <SectionSeparatorWrapper v-if="formResponse.searchType === searchType[0].returnValue" :separator="false">
      <div class="form-section">
        <FormField
          labelText="Provider number"
          placeholder="e.g. 1234567Y"
          type="text"
          v-model.lazy.trim="v$.formResponse.providerNumber.$model"
          :error="v$.formResponse.providerNumber.$error"
          errorMessage="Provider numbers must be less than 30 characters and contain numbers and letters (no symbols or spaces)."
        />
      </div>
    </SectionSeparatorWrapper>

    <!-- Search by name and location -->
    <template v-else-if="formResponse.searchType === searchType[1].returnValue">
      <SectionSeparatorWrapper :separator="false">
        <div class="form-section">
          <FormField
            :required="false"
            labelText="Surname"
            placeholder="e.g. Watson"
            type="text"
            v-model.lazy.trim="v$.formResponse.providerSurname.$model"
            :error="v$.formResponse.providerSurname.$error"
            :errorMessage="surnameErrorMessage"
            forId="surname"
          />
        </div>
        <div class="form-section">
          <FormField
            labelText="Postcode"
            :required="false"
            forId="postcode"
            placeholder="e.g. 3000"
            type="number"
            v-model.lazy.trim="v$.formResponse.postcode.$model"
            :error="v$.formResponse.postcode.$anyDirty && v$.formResponse.postcode.$invalid"
            :errorMessage="ERROR_MESSAGES.POSTCODE"
            @keydown="maxPostCodeLength"
          />
        </div>

        <div class="form-section">
          <FormField labelText="State" key="state-selection">
            <ul class="radio-group small">
              <li v-for="state in states" :key="state.returnValue">
                <input type="radio" :id="state.returnValue" :value="state.returnValue.toLowerCase()" name="state" v-model.lazy="v$.formResponse.state.$model" />
                <label :for="state.returnValue">{{ state.display }}</label>
              </li>
            </ul>
          </FormField>
        </div>
      </SectionSeparatorWrapper>
    </template>

    <div class="form-page-nav">
      <section>
        <button class="secondary" @click="updatePage(currentPage - 1)">Back</button>
        <button @click="handleNextPage(pages.PROVIDER_SEARCH_RESULTS.pageNumber)" :disabled="v$.formResponse.$invalid || v$.formResponse.$error">Search providers</button>
      </section>
    </div>
  </fieldset>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import { alphaNum, required, requiredIf, maxLength } from '@vuelidate/validators';
import { AUSTRALIAN_STATES } from '@/constants';
import { stringWithInvalidSpace, validPostCode, maxPostCodeLength } from '@general/js/utils';
import { isEmptyString } from '@/utils';
import { JOIN_NOW_NAME_VALIDATOR } from '@general/js/validators';
import { ErrorStrings } from '@general/js/constants';
import { NameErrorMessage } from '@general/js/enums/ErrorMessageEnum';
import GABQnMANCStepsMixins from './GABQnMANC_StepsMixins';

export default {
  setup() { return { v$: useVuelidate() }; },

  name: 'GABQ-step-2',

  mixins: [GABQnMANCStepsMixins],

  data() {
    return {
      ERROR_MESSAGES: {
        POSTCODE: ErrorStrings.POSTCODE,
      },
      states: AUSTRALIAN_STATES,
      searchType: [
        {
          display: 'Provider number',
          returnValue: 'number',
        },
        {
          display: 'Provider name and location',
          returnValue: 'name-location',
        },
      ],
      formResponse: {},
    };
  },

  computed: {
    validationObj() {
      const isProviderNumber = this.formResponse.searchType === this.searchType[0]?.returnValue;
      const isNameLocation = this.formResponse.searchType === this.searchType[1]?.returnValue;

      return {
        formResponse: {
          searchType: { required },
          providerNumber: {
            maxLength: maxLength(30),
            alphaNum,
            required: requiredIf(() => isProviderNumber),
          },
          providerSurname: {
            JOIN_NOW_NAME_VALIDATOR: (value) => {
              if (!isNameLocation || isEmptyString(value)) return true;
              return JOIN_NOW_NAME_VALIDATOR.$validator ? JOIN_NOW_NAME_VALIDATOR.$validator(value) : JOIN_NOW_NAME_VALIDATOR(value);
            },
            stringWithInvalidSpace: (value) => {
              if (!isNameLocation || isEmptyString(value)) return true;
              return stringWithInvalidSpace(value);
            },
            maxLength: maxLength(160),
          },
          postcode: {
            validPostCode: (val) => {
              if (!isNameLocation || isEmptyString(val)) return true;
              return validPostCode(val);
            },
          },
          state: {
            required: requiredIf(() => isNameLocation),
          },
        },
      };
    },

    surnameErrorMessage() {
      if (this.v$.formResponse.providerSurname && this.v$.formResponse.providerSurname.$error) {
        if (this.v$.formResponse.providerSurname.maxLength.$invalid) {
          return NameErrorMessage.IS_OVER_LENGTH;
        }
        if (this.v$.formResponse.providerSurname.JOIN_NOW_NAME_VALIDATOR.$invalid || !stringWithInvalidSpace(this.v$.formResponse.providerSurname.$model)) {
          return NameErrorMessage.IS_INVALID;
        }
      }

      return '';
    },
  },

  watch: {
    'formResponse.searchType': {
      handler(newData) {
        // whenever searchType changes, reset everything except searchType
        this.formResponse = {
          selectedProvider: '',
          searchType: newData,
          providerNumber: '',
          providerSurname: '',
          postcode: '',
          state: '',
        };
      },
    },
  },

  methods: {
    maxPostCodeLength,
  },

  validations() {
    return this.validationObj;
  },
};
</script>

<style lang="scss" scoped>
.form-header {
  margin-bottom: 48px;

  h2 {
    margin-bottom: 0.25rem;
  }

  p {
    margin-bottom: 0;
  }
}
</style>
